// File: /opt/stacks/workout/app/src/components/workout/WorkoutCoachView.js
//
// Unified active-workout display used for both guided sessions and standalone
// timers. All data and callbacks are received via props — the parent
// (CoachView.js) is responsible for mapping context state to these props.

import React, { useState, useEffect, useRef } from "react";
import { percentOfMax, zoneColour } from "../../utils/heartRateZones";
import { motion, AnimatePresence } from "framer-motion";
import ConcentricProgress from "./ConcentricProgress";
import SegmentFace from "./SegmentFace";
import MacroProgress from "./MacroProgress";
import BlockPreviewSheet from "./BlockPreviewSheet";
import WorkoutControls from "./WorkoutControls";
import FreeTrainingGraph from "./FreeTrainingGraph";
import FreeTrainingLog from "./FreeTrainingLog";
import {
  REST_TARGETS,
  lastLoggedAt,
  openRest,
  restStatus,
  timelineArcs,
} from "../../utils/freeTrainingLog";
import { formatLapTime } from "../../utils/freeTraining";
import {
  getActiveTokens,
  getPhaseMotionTokens,
  getPhaseVisualType,
  isTransitionLabel,
} from "../../utils/planVisuals";
import { formatLabel } from "../../utils/formatLabel";
import { cn } from "../../lib/utils";
import {
  Pause,
  Play,
  Check,
  Clock,
  Info,
  XCircle,
  Heart,
  Minimize2,
  Maximize2,
  Bike,
  Bluetooth,
  Sailboat,
  Zap,
  Pencil,
  Plus,
  Hourglass,
} from "lucide-react";
import { formatDuration } from "../../utils/time";
import SessionPlanOutline from "./SessionPlanOutline";
import useViewportWidth, {
  useViewportHeight,
} from "../../hooks/useViewportWidth";
import usePhoneLandscape from "../../hooks/usePhoneLandscape";
import StopWorkoutDialog from "./StopWorkoutDialog";

// Coach centre-display modes. Each lists what live data it needs so the toggle
// only offers modes whose data is currently streaming (no Power option when no
// power is incoming, no HR options when no HR).
const CENTER_MODES = [
  { id: "time", label: "Time" },
  { id: "time_hr", label: "Time + HR" },
  { id: "hr", label: "HR" },
  { id: "power", label: "Power" },
  { id: "power_hr", label: "Power + HR" },
  { id: "power_time", label: "Power + Time" },
];
const CENTER_MODE_NEEDS = {
  time: { hr: false, power: false },
  time_hr: { hr: true, power: false },
  hr: { hr: true, power: false },
  power: { hr: false, power: true },
  power_hr: { hr: true, power: true },
  power_time: { hr: false, power: true },
};

// Modes that surface the phase's own value somewhere in the ring centre —
// as the main figure (time, time_hr) or as the sub (power_time). On a rep-based
// phase that value IS the rep count, so the prescription line below the centre
// doesn't repeat it. The remaining modes replace it with a pulse or a wattage.
const CENTER_MODES_SHOWING_PHASE_VALUE = new Set([
  "time",
  "time_hr",
  "power_time",
]);

// Phase visual type → the theme CSS variable holding its colour (HSL triplet).
// Drives the ambient light and the phase-change bloom behind the ring.
const PHASE_GLOW_VARS = {
  work: "--work-color",
  rest: "--rest-color",
  warmup: "--warmup-color",
  cooldown: "--cooldown-color",
  countin: "--countin-color",
  transition: "--transition-color",
};

// One tile of the landscape stats panel: a small label, a big number, a note.
const StatTile = ({ label, sub, wide = false, children }) => (
  <div
    className={cn(
      "rounded-xl border border-border/60 bg-card/60 px-3 py-2",
      wide && "col-span-2",
    )}
  >
    <p className="truncate text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
      {label}
    </p>
    <p className="text-4xl font-bold leading-tight tabular-nums">{children}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const WorkoutCoachView = ({
  // Header
  phaseType,
  phaseLabel,
  exerciseName,
  exerciseDescription,
  contextInfo,
  repDisplay, // e.g. "12 reps" — shown below name for rep-based
  loadDisplay, // e.g. "90 kg · 75% 1RM" for the set in progress
  // Opens the mid-session load editor. Absent (standalone timers, casting) the
  // load line renders as plain text exactly as before.
  onEditLoad,
  // Reps actually done (#97): the reps set just finished ({ label, setNumber,
  // actual, prescribed }) and the opener for its correction sheet. Offered
  // under the ring during the rest that follows, never while lifting.
  lastRepsSet = null,
  onEditReps,
  intensityTarget, // e.g. "RPE 8" or "70% 1RM" — shown below name for rep-based when available
  // Static checklist of movements for a time-capped block (AMRAP, #36). When
  // present, shown under the title as a reminder of the round to cycle through —
  // the single continuous countdown stays the centrepiece.
  checklistItems = null,

  // Ring
  ringSize = 390, // consider bringing this in from class above
  progressItems,
  displayValue, // Centre text (time or rep count)
  displaySubValue, // Sub-text below display (e.g. "reps")
  spinnerMode = false,

  // Count-in props
  countInRemaining = 3,
  countInTotal = 3, // TODO: User-configurable count-in length?

  // Ready gate (rep → timed handoff): a paused-but-counting-up "get ready"
  // screen. readyGateElapsed is the current gate's count-up ("faff") time;
  // faffTotal is the session running total. onConfirmReady starts the count-in.
  isReadyGate = false,
  readyGateElapsed = 0,
  faffTotal = 0,
  onConfirmReady,

  // Overlays
  isRepBased = false,
  // Inter-block Setup section: compact countdown + prominent skip + always-on
  // upcoming-block outline (see useWorkoutUiState.isSetupScreen). Issue #22.
  isSetupScreen = false,
  isPaused = false,
  isRunning = false,
  isFinished = false,
  isLast = false,

  // Control callbacks
  onPlayPause,
  onSkipNext,
  onSkipBack,
  onFinish,
  onRoundForward,
  onRoundBack,
  canGoBack = false,
  hasSkip = true,
  hasRoundSkip = false,

  // Read-only: someone watching a broadcast live session. The control row is
  // not rendered at all rather than rendered inert — a dead play button that
  // does nothing when tapped reads as a bug, and the ring tap-to-pause
  // gesture has no visible affordance to disable. Every callback above is
  // already null on a watcher, so this is belt and braces on top of that.
  readOnly = false,

  // Next up
  nextUpLabel,
  nextUpValue,
  // True when the upcoming queue item is timed (s/m). The active-transition
  // visual collapses to a simple "Get ready: <next>" header when the next
  // phase is rep-based — a countdown ring is less useful when the user will
  // self-pace the next phase. The engine still ticks the phase down so
  // total/remaining time stays accurate across forward/back skips.
  nextUpIsTimed = true,

  // Session plan outline (shown when paused)
  plan,
  // Opens the mid-session editor (#77). Supplied only where the session is a
  // real guided plan, so standalone timers show no affordance for it.
  onEditSession,
  // Extra pause-screen actions, as { key, icon, label, onClick }. Sits beside
  // "Edit session" in the same row and the same weight: things you can do to
  // the session while it is stopped, rather than to the workout itself. Live
  // sessions use it to reach the share link and the user's hardware; a solo
  // workout passes none and the row is what it always was.
  pauseActions = [],
  currentPath,
  loopCounters,
  phaseLog,
  currentPhaseElapsedSec,
  standaloneTimer,

  // Time remaining
  totalRemaining,
  finishBy = null,
  endTime,

  // Stop Workout dialog
  stopActions = [], // [{ label, onClick, variant?, withFeedback? }]
  // When true, the "Stop workout?" dialog embeds the optional effort/notes
  // fields inline; any stop action flagged withFeedback receives { rpe, notes }.
  collectStopFeedback = false,

  // Heart rate
  currentBpm = null,
  // 1-5, for colouring the heart rate by zone; null when unknown.
  hrZone = null,
  // heartRateBasis() of whoever's heart rate this is (max, resting, method):
  // % of max and the zone scale in the heart-rate views. Null when unknown.
  hrBasis = null,
  // A phone on its side (#149): the bike or rower, shown beside the ring
  // instead of on a swipe-across page.
  sidePanel = null,

  // Free training (#146, #157): { log, list, onChange, elapsed, hr, zone,
  // subscribe, hrSource }. The ring becomes a dial of what you've logged, a
  // pill switches it for the heart-rate and power chart, and the log and the
  // box to add to it sit under the controls.
  freeTraining = null,

  // FTMS-connected equipment (Web Bluetooth, direct-to-browser). `activeDevice`
  // is the entry from ConnectedEquipmentContext that's currently streaming.
  // For Phase 1 supports indoor bike + rower; secondary cue (rpm/spm) flips
  // based on activeDevice.type.
  activeDevice = null,
  showAddDevice = false,
  onAddDevice,

  // Visual preferences
  drainTimer = false,
  drainSegments = false,

  // Coach centre display mode (Time / HR / Power / combos) + setter.
  centerMode = "time",
  onSelectCenterMode,

  // Macro-progress (session block stepper)
  blocks = [],
  activeGroupInfo = null,
  pauseOnMacroTap = true,

  // Immersive mode
  isImmersive = false,
  onEnterImmersive,
  onExitImmersive,
}) => {
  const [showDescription, setShowDescription] = useState(false);
  // The ring never grows past the screen: at its 390 px default it overflowed
  // a 390 px phone (16 px padding each side), clipping its edges and pushing
  // the timer digits into the stroke.
  const viewportWidth = useViewportWidth();
  // Desktop with a guided session: two columns, ring left and plan right, so
  // the ring can grow and the plan stays in view instead of hiding until pause.
  const desktop = viewportWidth >= 1024 && !!plan;
  const viewportHeight = useViewportHeight();
  // A phone on its side (#149), e.g. mounted on a rower: the desktop's two
  // columns, scaled to a short screen. Free training keeps its stacked log.
  const phoneLandscape = usePhoneLandscape();
  const landscape = !desktop && !!plan && !freeTraining && phoneLandscape;
  // Free training on a phone on its side (#227): the clock on the left and
  // the log beside it, instead of the log sitting below the fold.
  const freeSide = !desktop && !!freeTraining && phoneLandscape && !isFinished && !readOnly;
  const wide = desktop || landscape;
  // On desktop the ring also has to leave room above it (app bar, progress
  // strip, exercise name) and below it (controls) within the window height.
  const ringPx = desktop
    ? Math.max(280, Math.min(440, viewportHeight - 470))
    : landscape || freeSide
      ? // Room for the step strip above and the controls below.
        Math.max(140, Math.min(viewportHeight - 170, Math.round(viewportWidth * 0.4)))
    : freeTraining
      ? // Free training: the ring stays put above a scrolling log (#164).
        Math.max(200, Math.min(ringSize, viewportWidth - 32, Math.round(viewportHeight * 0.3)))
      : Math.max(220, Math.min(ringSize, viewportWidth - 32));
  const [tapFeedback, setTapFeedback] = useState(null);
  const [previewBlock, setPreviewBlock] = useState(null);
  // Free training's graphic: the dial ("time") or the chart ("graph").
  const [freeView, setFreeView] = useState("time");

  const activeTokens = getActiveTokens(phaseType, phaseLabel);
  const motionTokens = getPhaseMotionTokens(phaseType, phaseLabel);

  // Setup section into a reps phase: surface the upcoming exercise prominently
  // instead of the "Setup" countdown ring. The phase still runs in the engine —
  // only the header copy changes.
  const isQuietTransition =
    isTransitionLabel(phaseLabel) && !nextUpIsTimed && !!nextUpLabel;
  const displayedExerciseName = isQuietTransition
    ? `Get ready: ${formatLabel(nextUpLabel)}`
    : formatLabel(exerciseName);

  // Phase identity for the transition choreography. `phaseKey` changes when
  // the phase hand-off happens (colour family or movement changes) and NEVER
  // on a timer tick, so it can key one-shot animations: the title/badge
  // crossfade and the colour bloom behind the ring.
  const phaseVisualType = getPhaseVisualType(phaseType, phaseLabel);
  const phaseGlowVar = PHASE_GLOW_VARS[phaseVisualType] || PHASE_GLOW_VARS.work;
  const phaseKey = `${phaseVisualType}:${displayedExerciseName}`;

  // On setup screens the outline is the focus, so the ring de-emphasises to a
  // single muted countdown — same size, but no inner block/round rings and a
  // subtle grey stroke instead of the phase colour.
  // Free training's dial: an hour round (two once you pass it), each stretch
  // coloured by what you logged at its end.
  const freeElapsed = freeTraining ? Math.max(0, freeTraining.elapsed || 0) : 0;
  // A rest marked with the Rest button: open (counts up) or timed.
  const freeRest = freeTraining ? openRest(freeTraining.rests) : null;
  const freeRestStatus = restStatus(freeRest, freeElapsed);
  const ringItems = freeTraining
    ? [
        {
          timeline: {
            arcs: timelineArcs(freeTraining.log, freeTraining.rests, freeElapsed),
            span: 3600 * Math.max(1, Math.ceil(freeElapsed / 3600)),
            now: freeElapsed,
          },
        },
      ]
    : isSetupScreen
      ? progressItems
          ?.slice(0, 1)
          .map((it) => ({ ...it, type: "muted" })) || []
      : progressItems;

  // ─── Final-countdown urgency (last 3 seconds of a timed phase) ────────────
  // Derived from the outer ring's own progress item rather than a new prop —
  // useWorkoutUiState always puts the phase's raw {current,total,reverse} at
  // progressItems[0] once count-in/ready-gate are past. `reverse` phases
  // (rest) already store remaining seconds in `current`; non-reverse phases
  // (work) store elapsed, so `total - current` recovers remaining there too.
  const primaryProgressItem = progressItems?.[0];
  const timeLeftInPhaseSec =
    primaryProgressItem &&
    !primaryProgressItem.indeterminate &&
    !primaryProgressItem.discrete &&
    !primaryProgressItem.segments &&
    Number.isFinite(primaryProgressItem.total) &&
    primaryProgressItem.total > 0
      ? primaryProgressItem.reverse
        ? primaryProgressItem.current
        : primaryProgressItem.total - primaryProgressItem.current
      : null;
  const secondsLeftDisplay =
    timeLeftInPhaseSec != null ? Math.ceil(timeLeftInPhaseSec) : null;
  const isFinalCountdown =
    !isSetupScreen &&
    !isRepBased &&
    !spinnerMode &&
    secondsLeftDisplay != null &&
    secondsLeftDisplay >= 1 &&
    secondsLeftDisplay <= 3;

  const handleBlockTap = (block) => {
    if (pauseOnMacroTap && isRunning) onPlayPause?.();
    setPreviewBlock(block);
  };

  // Auto-collapse description when exercise changes
  useEffect(() => {
    setShowDescription(false);
  }, [exerciseName]);

  // Clear tap feedback
  useEffect(() => {
    if (!tapFeedback) return;
    const durationMs = tapFeedback === "confirm" ? 450 : 600;
    const t = setTimeout(() => setTapFeedback(null), durationMs);
    return () => clearTimeout(t);
  }, [tapFeedback]);

  const handleRepAdvance = () => {
    if (isFinished || readOnly) return;
    setTapFeedback("confirm");

    if (isLast) {
      onFinish?.();
      return;
    }

    onSkipNext?.();
  };

  const handleCircleTap = () => {
    // A watcher's tap must not even flash the pause feedback — nothing they
    // do reaches the host's session.
    if (isFinished || readOnly) return;

    if (isRepBased && !isPaused) {
      handleRepAdvance();
      return;
    }

    setTapFeedback(isRunning ? "pause" : "play");
    onPlayPause?.();
  };

  // ─── Tap vs swipe on the ring ──────────────────────────────────────────────
  // The overlay covers the whole ring, so a swipe-to-page gesture that starts
  // on it must NOT register as a tap (which would pause the workout). We track
  // the pointer and only treat a release as a tap when it barely moved and was
  // brief; a larger drag is a scroll/swipe handled by the pager (touch-action
  // pan-x). Keyboard activation is preserved via onClick (detail === 0).
  const ringPointerRef = useRef(null);
  const handleRingPointerDown = (e) => {
    ringPointerRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };
  const handleRingPointerUp = (e) => {
    const start = ringPointerRef.current;
    ringPointerRef.current = null;
    if (!start) return;
    const moved =
      Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10;
    if (!moved && Date.now() - start.t < 300) handleCircleTap();
  };
  const handleRingClick = (e) => {
    // Pointer taps fire via onPointerUp; only keyboard activations (Enter /
    // Space) reach here with detail === 0.
    if (e.detail === 0) handleCircleTap();
  };

  const tapOverlayLabel = isRepBased
    ? isPaused
      ? "Resume"
      : isLast
        ? "Finish workout"
        : "Complete reps"
    : isRunning
      ? "Pause"
      : "Resume";
  const pausedPulseDurationMs = Math.max(motionTokens.dotPulseMs, 1600);

  // ─── Centre display mode ───────────────────────────────────────────────────
  // Resolve which value(s) fill the ring centre. Modes needing HR/power are
  // only offered when that data is live; if the saved mode's data disappears
  // mid-workout we fall back to Time so the centre is never blank.
  const centerPowerW = activeDevice?.snapshot?.power_watts;
  const hasHr = currentBpm != null;
  const hasPower = centerPowerW != null;
  const availableCenterModes = CENTER_MODES.filter(
    (m) =>
      (!CENTER_MODE_NEEDS[m.id].hr || hasHr) &&
      (!CENTER_MODE_NEEDS[m.id].power || hasPower),
  );
  const effectiveCenterMode = availableCenterModes.some((m) => m.id === centerMode)
    ? centerMode
    : "time";
  const bpmText = hasHr ? String(Math.round(currentBpm)) : "—";
  const powerText = hasPower ? String(Math.round(centerPowerW)) : "—";
  let centerMain = displayValue;
  let centerSub = displaySubValue;
  const hrPct = hasHr ? percentOfMax(currentBpm, hrBasis?.max) : null;
  if (effectiveCenterMode === "hr") {
    centerMain = bpmText;
    centerSub = hrPct != null ? `bpm · ${hrPct}% max` : "bpm";
  } else if (effectiveCenterMode === "power") {
    centerMain = powerText;
    centerSub = "watts";
  } else if (effectiveCenterMode === "time_hr") {
    centerMain = displayValue;
    centerSub = `${bpmText} bpm`;
  } else if (effectiveCenterMode === "power_hr") {
    centerMain = powerText;
    centerSub = `${bpmText} bpm`;
  } else if (effectiveCenterMode === "power_time") {
    centerMain = powerText;
    centerSub = displayValue;
  }

  // Prescription for the set in progress, drawn inside the ring beneath the
  // centre readout (issue #74). The rep count is only carried here when the
  // centre isn't already showing it — in the modes below it is the main figure
  // or the sub, and repeating it would just be noise. Switch the centre to
  // heart rate or power and the count would otherwise vanish entirely, which is
  // exactly what this line is for. Both figures come off the per-set-resolved
  // node, so an 8/6/5/4 @ 80/85/90/95 ramp reads its own set, not set 1's.
  // Stacked, never joined: the centre is a column and its narrowest usable
  // width is at the bottom of the stack, so reps and load each get their own
  // row rather than competing for one. Load keeps its own dimensions together
  // ("85 kg · 71% 1RM") — the percentage qualifies the weight, it isn't a
  // separate fact.
  const centreAlreadyShowsReps =
    isRepBased && CENTER_MODES_SHOWING_PHASE_VALUE.has(effectiveCenterMode);
  const prescriptionLines = [
    centreAlreadyShowsReps ? null : repDisplay,
    loadDisplay,
  ].filter(Boolean);

  // The load line doubles as the way into the mid-session editor (issue #76):
  // the figure you want to change is the one you tap. A rep-based phase with no
  // load yet gets a quiet placeholder instead, since "add the weight I actually
  // used" is the same gesture — but only for rep-based work, so a bodyweight
  // circuit never grows an affordance for a number it will not carry.
  const loadIsEditable = !!onEditLoad && !isFinished && countInRemaining <= 0;
  const showAddLoad = loadIsEditable && !loadDisplay && isRepBased;

  // Final-countdown digit pulse: a brief scale/opacity pop each second, keyed
  // on the second itself so it retriggers every tick of the last 3 seconds —
  // otherwise `centerMain` renders plainly, unchanged from before.
  const centerMainNode = isFinalCountdown ? (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={secondsLeftDisplay}
        initial={{ opacity: 0.4, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="inline-block"
      >
        {centerMain}
      </motion.span>
    </AnimatePresence>
  ) : (
    centerMain
  );

  // Timer digits scale with the ring and with how many characters they show,
  // so "02:57" always sits inside the stroke while "10" can be big.
  const centerChars = String(centerMain ?? "").length;
  // On its side (#149) there's no ring to clear: the clock fills the face's
  // height, and its width for "02:57".
  const faceW = Math.round(viewportWidth * 0.58 - 32);
  // Less when a pill (Add weight, reps done) sits under the squares.
  // Free training's heart-rate view needs more room than the dial: the box
  // widens to the screen (portrait) or the column (on its side) and grows
  // taller, and goes back to the ring with the Time pill.
  const freeGraphShown = !!freeTraining && !isFinished && freeView === "graph" && (hasHr || hasPower);
  const freeGraphBox = freeGraphShown
    ? freeSide
      ? {
          width: Math.max(ringPx, Math.round(viewportWidth * 0.5 - 40)),
          height: Math.max(ringPx, viewportHeight - 170),
        }
      : {
          width: Math.max(ringPx, Math.min(480, viewportWidth - 24)),
          height: Math.max(ringPx, Math.min(380, Math.round(viewportHeight * 0.44))),
        }
    : null;
  const facePill = (showAddLoad && !readOnly) || (!!lastRepsSet && !!onEditReps && !isRepBased && !isFinished && !readOnly);
  const faceH = Math.max(100, viewportHeight - 236 - (facePill ? 46 : 0));
  const faceRound = landscape ? "rounded-2xl" : "rounded-full";
  const centerFontPx = landscape
    ? Math.round(Math.min(150, faceH * 0.72, faceW * (centerChars >= 5 ? 0.2 : centerChars >= 4 ? 0.26 : 0.36)))
    : Math.round(
        // ~20% of the ring keeps "02:57" clear of the inner stroke down to a
        // 360 px phone (24% touched it there).
        Math.min(96, ringPx * (centerChars >= 5 ? 0.2 : centerChars >= 4 ? 0.24 : 0.32)),
      );

  // Prescription lines under the figure sit where the circle narrows, so they
  // scale with the ring, and a load's qualifier ("40% 1RM") drops to its own
  // smaller line instead of running the full width into the stroke.
  const loadFontPx = Math.round(Math.min(24, ringPx * 0.065));
  const renderPrescription = (line) => {
    const [main, qualifier] = String(line).split(" · ");
    return (
      <>
        <span className="block" style={{ fontSize: loadFontPx }}>
          {main}
        </span>
        {qualifier && (
          <span
            className="block text-foreground/50"
            style={{ fontSize: Math.round(loadFontPx * 0.7) }}
          >
            {qualifier}
          </span>
        )}
      </>
    );
  };

  // ─── READY GATE OVERLAY ────────────────────────────────────────────────────
  // Shown after finishing a rep set when the next phase is timed: the user gets
  // to / sets up the station while a count-up "faff" timer runs. Tapping Ready
  // starts the count-in, then the clock.
  if (isReadyGate) {
    return (
      <div className="flex flex-col items-center h-full p-4 text-center max-w-lg mx-auto gap-4 overflow-y-auto overscroll-contain">
        <div className="flex flex-col items-center gap-1 mt-2">
          <p className="text-sm font-bold uppercase tracking-widest text-transition">
            Next up
          </p>
          <p className="text-3xl lg:text-4xl font-bold">
            {formatLabel(exerciseName)}
          </p>
          {nextUpValue && (
            <p className="text-base text-muted-foreground tabular-nums">
              {nextUpValue}
            </p>
          )}
        </div>

        <div className="flex flex-col items-center gap-1">
          <div
            className="flex items-center gap-2 text-5xl font-mono font-bold text-transition tabular-nums"
            aria-live="off"
          >
            <Clock className="h-8 w-8" />
            <span>{formatDuration(readyGateElapsed)}</span>
          </div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Getting ready
            {faffTotal > 0 ? ` · ${formatDuration(faffTotal)} total` : ""}
          </p>
        </div>

        {onConfirmReady && (
          <button
            onClick={onConfirmReady}
            aria-label="Ready — start the count-in"
            className="flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 px-10 py-4 text-lg font-bold text-white shadow-lg active:scale-95 transition"
          >
            <Check className="h-6 w-6" />
            Ready
          </button>
        )}

        {/* Keep the session outline visible so the user can see what's coming.
            Full plan with the active exercise highlighted + auto-scrolled into
            view (same as the pause screen). */}
        {plan && (
          <SessionPlanOutline
            plan={plan}
            currentPath={currentPath}
            loopCounters={loopCounters}
            phaseLog={phaseLog}
            currentPhaseElapsedSec={currentPhaseElapsedSec}
            maxHeight="max-h-[40vh]"
            className="w-full max-w-md mx-auto mt-1"
          />
        )}
      </div>
    );
  }
  // ──────────────────────────────────────────────────────────────────────────

  // ─── COUNT-IN ──────────────────────────────────────────────────────────────
  // "Get ready, 3, 2, 1" runs inside the normal Coach layout: the same header
  // (the step about to start), the same ring in the same place, with the
  // count in its centre. It used to be a screen of its own, top-aligned, so
  // the ring jumped when the first step began.
  const countingIn = countInRemaining > 0 && !isFinished;
  const countInItems = [
    {
      segments: countInTotal,
      // The active (bright) segment is drawn at index `completed`, with
      // earlier segments dimmed. So `completed = elapsed count` marches the
      // highlight 1→2→3 in step with the "3, 2, 1" countdown (no +1, which
      // started the highlight on the 2nd bar and left "1" highlighting none).
      completed: countInTotal - countInRemaining,
      type: "countin",
      discrete: true,
    },
  ];
  // ──────────────────────────────────────────────────────────────────────────

  // Next-up, outline, clock and actions render in place on a phone and move
  // into a right-hand panel on desktop (two columns: big ring left, plan
  // right), so they are built once here.
  const nextUpEl = (
    <>
      {/* Next exercise preview — the outline already lists what's next on
          setup screens, so suppress the standalone line there. */}
      {nextUpLabel && !isSetupScreen && (
        <p className="text-xl text-text-muted animate-fade-in">
          Next: {nextUpLabel}
          {nextUpValue && (
            <span className="ml-2 text-muted-foreground">— {nextUpValue}</span>
          )}
        </p>
      )}

    </>
  );
  const outlineEl = (
    <>
      {/* Session plan outline — shown on pause, and as the main content while
          setting up between blocks (single render covers both states). On
          setup screens we narrow it to just the upcoming block. */}
      {/* Free training's plan is only the stopwatch: its log is the outline. */}
      {(isPaused || isSetupScreen) && (plan || standaloneTimer) && !freeTraining && (
        <div className="w-full max-w-md mx-auto mb-2">
          <SessionPlanOutline
            plan={plan}
            currentPath={currentPath}
            loopCounters={loopCounters}
            phaseLog={phaseLog}
            currentPhaseElapsedSec={currentPhaseElapsedSec}
            standaloneTimer={standaloneTimer}
            upcomingOnly={isSetupScreen}
            maxHeight={isSetupScreen ? "max-h-[55vh]" : "max-h-[40vh]"}
            onEdit={
              isPaused && !isFinished && !isSetupScreen && plan
                ? onEditSession
                : undefined
            }
          />
        </div>
      )}

    </>
  );
  // Phone landscape with nothing beside the face (no bike or rower panel):
  // the right column shows big tiles for heart rate, round and what's next
  // instead of leaving the space empty (#227).
  const statsPanel = landscape && !sidePanel && isRunning && !isSetupScreen && !isFinished;

  const footerEl = (
    <>
      {/* Footer: one clock (time remaining + end time) + HR + active device.
          The ring already shows set and phase, so they aren't repeated here. */}
      {(totalRemaining > 0 ||
        currentBpm != null ||
        activeDevice?.snapshot?.power_watts != null ||
        (showAddDevice && onAddDevice) ||
        activeDevice?.status === "reconnecting") && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-base text-white/60 mb-2 [&>.sep]:hidden">
          {totalRemaining > 0 && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatDuration(totalRemaining)} remaining</span>
            </div>
          )}
          {totalRemaining > 0 && endTime && (
            <>
              <span className="sep text-white/30">·</span>
              <span>Ends ~{endTime}</span>
            </>
          )}
          {/* Finish-by deadline (#8): amber once the projection runs past it. */}
          {totalRemaining > 0 && finishBy && (
            <>
              <span className="sep text-white/30">·</span>
              <span className={finishBy.overSec > 60 ? "text-amber-400" : undefined}>
                {`by ${finishBy.label}`}
              </span>
            </>
          )}
          {currentBpm != null && !statsPanel && (
            <>
              {totalRemaining > 0 && <span className="sep text-white/30">·</span>}
              <div className="flex items-center gap-1">
                <Heart
                  className="w-3.5 h-3.5 animate-pulse"
                  style={{ color: hrZone ? zoneColour(hrZone) : "#ef4444" }}
                />
                <span className="tabular-nums" style={hrZone ? { color: zoneColour(hrZone) } : undefined}>
                  {currentBpm}
                </span>
              </div>
            </>
          )}
          {activeDevice?.snapshot?.power_watts != null ? (
            <>
              <span className="sep text-white/30">·</span>
              <div className="flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-yellow-400" />
                <span className="tabular-nums">
                  {Math.round(activeDevice.snapshot.power_watts)} W
                </span>
                {activeDevice.type === "rower" &&
                  activeDevice.snapshot.stroke_rate_spm != null && (
                    <span className="tabular-nums text-white/50">
                      · {Math.round(activeDevice.snapshot.stroke_rate_spm)} spm
                    </span>
                  )}
                {activeDevice.type === "indoor_bike" &&
                  activeDevice.snapshot.cadence_rpm != null && (
                    <span className="tabular-nums text-white/50">
                      · {Math.round(activeDevice.snapshot.cadence_rpm)} rpm
                    </span>
                  )}
              </div>
            </>
          ) : activeDevice?.status === "reconnecting" ? (
            <>
              <span className="sep text-white/30">·</span>
              <div className="flex items-center gap-1 text-white/40">
                {activeDevice.type === "rower" ? (
                  <Sailboat className="w-3.5 h-3.5 animate-pulse" />
                ) : (
                  <Bike className="w-3.5 h-3.5 animate-pulse" />
                )}
                <span>
                  {activeDevice.hint === "wake_device"
                    ? "Reconnecting — pull the handle"
                    : "Reconnecting…"}
                </span>
              </div>
            </>
          ) : showAddDevice && onAddDevice ? (
            <>
              <span className="sep text-white/30">·</span>
              <button
                type="button"
                onClick={onAddDevice}
                className="flex items-center gap-1 rounded-full border border-white/15 px-2 py-0.5 text-sm text-white/70 hover:text-white hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Bluetooth className="w-3.5 h-3.5" />
                <span>Connect equipment</span>
              </button>
            </>
          ) : null}
        </div>
      )}
    </>
  );
  const actionsEl = (
    <>
      {/* Edit session / Stop Workout — pinned bottom zone. (Wake-lock is now
          controlled from Settings → Audio & Display.) */}
      {/* Wraps: a live session can put up to four actions here (send to
          device, share, edit, stop) and a narrow phone must not squeeze them
          into an unreadable row. */}
      <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
        {/* Paused only, and never on a setup screen — you are mid-changeover
            with a clock running, which is the wrong moment to be handed the
            whole session to rewrite. Sits beside Stop Workout rather than under
            the plan outline: same row, same weight, and it stays put instead of
            drifting below the fold behind a long outline. */}
        {isPaused &&
          !isFinished &&
          !isSetupScreen &&
          pauseActions.map(({ key, icon: ActionIcon, label, onClick }) => (
            <button
              key={key || label}
              type="button"
              onClick={onClick}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors p-2 mt-1 mb-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={label}
            >
              {ActionIcon && <ActionIcon className="h-4 w-4" />}
              <span>{label}</span>
            </button>
          ))}
        {isPaused && !isFinished && !isSetupScreen && plan && onEditSession && (
          <button
            type="button"
            onClick={onEditSession}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors p-2 mt-1 mb-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Edit session"
          >
            <Pencil className="h-4 w-4" />
            <span>Edit session</span>
          </button>
        )}
        {stopActions.length > 0 &&
          (isFinished ? (
            <button
              onClick={stopActions[0]?.onClick}
              className="px-6 py-2 mt-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors btn-neon"
            >
              {stopActions[0]?.label || "Done"}
            </button>
          ) : (
            isPaused && (
              <StopWorkoutDialog
                stopActions={stopActions}
                collectStopFeedback={collectStopFeedback}
                trigger={
                  <button
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors p-2 mt-1 mb-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Stop workout"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Stop workout</span>
                  </button>
                }
              />
            )
          ))}
      </div>
    </>
  );

  const workoutControlsEl = !isFinished && !readOnly && (
    <WorkoutControls
      isRepBased={isRepBased}
      isRunning={isRunning}
      isLast={isLast}
      canGoBack={canGoBack}
      hasSkip={hasSkip}
      // Setup sections are inter-block top-level phases, so the round-skip
      // chevrons aren't meaningful — force them off.
      hasRoundSkip={isSetupScreen ? false : hasRoundSkip}
      onPlayPause={onPlayPause}
      onSkipNext={isRepBased ? handleRepAdvance : onSkipNext}
      onSkipBack={onSkipBack}
      // Free training has no plan to step back through. Its Rest button
      // sits there instead: tap to rest, tap again (or log) to stop.
      hideBack={!!freeTraining}
      backSlot={
        freeTraining?.onRest ? (
          <button
            type="button"
            onClick={freeTraining.onRest}
            disabled={!isRunning && !freeRest}
            aria-pressed={!!freeRest}
            aria-label={freeRest ? "End rest" : "Rest"}
            title={freeRest ? "End rest" : "Rest"}
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-full text-rest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
              freeRest ? "bg-rest/15 ring-1 ring-rest/50" : "hover:bg-rest/10",
            )}
          >
            <Hourglass className="h-6 w-6" />
          </button>
        ) : null
      }
      onRoundForward={onRoundForward}
      onRoundBack={onRoundBack}
      onFinish={isRepBased ? handleRepAdvance : onFinish}
    />
  );
  // Hidden but holding their place during the count-in, so the ring and the
  // rest of the screen don't move when the step starts.
  const controlsEl =
    workoutControlsEl && countingIn ? (
      <div className="invisible pointer-events-none" aria-hidden="true">
        {workoutControlsEl}
      </div>
    ) : (
      workoutControlsEl
    );

  const coachColumn = (
    <>
      {/* Fullscreen toggle — top-right */}
      {isImmersive && onExitImmersive && (
        <button
          onClick={onExitImmersive}
          aria-label="Exit fullscreen"
          title="Exit fullscreen"
          className="absolute top-2 right-2 z-30 p-2 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Minimize2 className="h-5 w-5" />
        </button>
      )}
      {!isImmersive && onEnterImmersive && (
        <button
          onClick={onEnterImmersive}
          aria-label="Enter fullscreen"
          title="Enter fullscreen"
          className="absolute top-2 right-2 z-30 p-2 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Maximize2 className="h-5 w-5" />
        </button>
      )}

      {/* Macro-progress stepper — pinned top zone. Symmetric px clears the
          absolute top-right fullscreen button so block pills/dots never sit
          under it (and stay visually centred). */}
      <div className="shrink-0 w-full px-10">
        <MacroProgress
          blocks={blocks}
          activeGroupInfo={activeGroupInfo}
          onBlockTap={handleBlockTap}
        />
      </div>
      <BlockPreviewSheet
        block={previewBlock}
        open={!!previewBlock}
        onOpenChange={(next) => {
          if (!next) setPreviewBlock(null);
        }}
      />

      {/* Scrolling middle zone — only this region scrolls in immersive mode,
          keeping the top (macro-progress + fullscreen icon) and bottom controls
          pinned. */}
      <div
        className={cn(
          "w-full flex flex-col items-center gap-2",
          freeTraining && !isFinished
            ? "flex-1 min-h-0"
            : isImmersive &&
                "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain",
        )}
      >
        {/* Header. Free training has no phases to name: its title is a small
            label under the controls instead, leaving room for the log. */}
        {!freeTraining && (
        <div className="w-full flex flex-col items-center">
        <div className="flex items-center justify-center gap-2 mt-1 px-4 w-full">
          {/* Phase hand-off crossfade: keyed on the phase identity so the
              outgoing name lifts away while the incoming one rises in with its
              own phase colour. popLayout keeps the exiting element out of flow
              so the header never double-stacks. */}
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.h1
              key={phaseKey}
              initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -14, filter: "blur(6px)" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className={cn(
                landscape
                  ? "text-2xl font-bold text-center leading-tight pb-1"
                  : "text-4xl lg:text-5xl font-bold text-center leading-tight pb-2",
                activeTokens.text,
                activeTokens.glow,
              )}
            >
              {displayedExerciseName}
            </motion.h1>
          </AnimatePresence>
          {exerciseDescription && (
            <button
              onClick={() => setShowDescription((prev) => !prev)}
              className="flex-shrink-0 p-1 mb-2 rounded-full text-white/50 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={
                showDescription
                  ? "Hide exercise description"
                  : "Show exercise description"
              }
            >
              <Info className="w-5 h-5" />
            </button>
          )}
        </div>

        {showDescription && exerciseDescription && (
          <div className="mx-4 mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70 leading-relaxed animate-in fade-in slide-in-from-top-1 duration-200">
            {exerciseDescription}
          </div>
        )}


        {/* Dynamic Intensity Target Badge - Now elevated right below the title */}
        <AnimatePresence mode="popLayout" initial={false}>
          {intensityTarget && (
            <motion.div
              key={intensityTarget}
              initial={{ opacity: 0, y: 8, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.92 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={cn(
                "mt-1 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold tracking-wide",
                activeTokens.bg,
                activeTokens.border,
              )}
            >
              <span className={cn("opacity-80", activeTokens.text)}>
                {" "}
                Target:
              </span>
              <span className="text-white">{intensityTarget}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AMRAP / time-capped checklist (#36): the round of movements to cycle
            through for the duration of the cap. Display-only. */}
        {checklistItems?.length > 0 && (
          <ul className="mx-auto mt-2 w-fit max-w-xs space-y-1 text-left">
            {checklistItems.map((item, i) => (
              <li
                key={item.id || i}
                className="flex items-baseline justify-between gap-3 text-sm text-white/80"
              >
                <span className="font-medium">{item.label}</span>
                {item.detail && (
                  <span className="text-white/55 tabular-nums">
                    {item.detail}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

      </div>
      )}

      {/* Free training: the dial, or the Coach's chart once there's heart
          rate or power to draw. */}
      {freeTraining && !isFinished && (hasHr || hasPower) && (
        <div className="flex flex-wrap items-center justify-center gap-1">
          {[
            { id: "time", label: "Time" },
            { id: "graph", label: hasPower ? "Power + HR" : "Heart rate" },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setFreeView(m.id)}
              aria-pressed={freeView === m.id}
              className={cn(
                "px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                freeView === m.id
                  ? "bg-white/85 text-black"
                  : "bg-white/10 text-white/60 hover:text-white",
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Centre-display toggle — only when there's more than just Time to show */}
      {!isFinished &&
        !freeTraining &&
        onSelectCenterMode &&
        availableCenterModes.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1">
            {availableCenterModes.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onSelectCenterMode(m.id)}
                aria-pressed={effectiveCenterMode === m.id}
                className={cn(
                  "px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  effectiveCenterMode === m.id
                    ? "bg-white/85 text-black"
                    : "bg-white/10 text-white/60 hover:text-white",
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

      {/* Ring + centre display. On its side (#149) the ring gives way to a
          wide face with bars and squares under it, like the Khronos timer. */}
      <div
        className={cn("relative flex items-center justify-center", landscape ? "my-0" : "my-4")}
        style={
          landscape
            ? { width: faceW, height: faceH }
            : freeGraphBox || { width: ringPx, height: ringPx }
        }
      >
        {/* The steady-state ambient wash that used to sit here — a large radial
            phase-colour light filling the ring's interior — is gone. It tinted
            the whole centre of the screen red for the entire working set, which
            read as glare rather than atmosphere. The ring keeps its own stroke
            glow (drop-shadow-glow-*, ConcentricProgress), and the two bounded,
            state-tied blooms below still fire on their own events. */}

        {/* One-shot bloom on the phase hand-off: a pulse of the incoming
            phase colour that swells out from the ring and dissipates. Keyed
            on phase identity so it fires exactly once per transition. */}
        {!isSetupScreen && (
          <motion.div
            key={phaseKey}
            aria-hidden
            initial={{ opacity: 0.4, scale: 0.7 }}
            animate={{ opacity: 0, scale: 1.18 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(closest-side, hsl(var(${phaseGlowVar}) / 0.45) 30%, hsl(var(${phaseGlowVar}) / 0.15) 58%, transparent 75%)`,
            }}
          />
        )}

        {/* Final-countdown pulse: the last 3 seconds of a timed phase get an
            extra, faster bloom per second on top of the steady-state glow
            above — a quiet urgency cue that costs nothing outside that
            3-second window (unmounted otherwise). */}
        {isFinalCountdown && (
          <motion.div
            key={`countdown-${secondsLeftDisplay}`}
            aria-hidden
            initial={{ opacity: 0.55, scale: 0.88 }}
            animate={{ opacity: 0, scale: 1.08 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(closest-side, hsl(var(${phaseGlowVar}) / 0.6) 30%, transparent 70%)`,
            }}
          />
        )}

        {/* Hidden under free training's chart, or its glow shows past it. */}
        <div
          className={cn(
            freeTraining && freeView === "graph" && (hasHr || hasPower) && "invisible",
          )}
        >
          {!landscape && (
            <ConcentricProgress
              items={countingIn ? countInItems : ringItems}
              size={ringPx}
              strokeWidth={12}
              spinnerMode={countingIn ? false : spinnerMode}
              paused={!isRunning}
              drainTimer={countingIn ? null : drainTimer}
              drainSegments={countingIn ? null : drainSegments}
            />
          )}
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {isFinished ? (
            <span className="text-4xl font-bold text-green-400">Done!</span>
          ) : countingIn ? (
            <div className="flex flex-col items-center text-center">
              <span className="text-sm font-bold uppercase tracking-widest text-countin drop-shadow-glow-countin">
                Get ready
              </span>
              <span
                key={countInRemaining}
                className="font-mono font-bold leading-none text-countin tabular-nums animate-in zoom-in duration-300"
                style={{ fontSize: landscape ? Math.round(Math.min(150, faceH * 0.6)) : Math.round(Math.min(128, ringPx * 0.4)) }}
              >
                {countInRemaining}
              </span>
            </div>
          ) : freeTraining ? (
            // The session clock, and how long since you last logged.
            <div className="text-center">
              <span
                className="text-8xl font-timer font-extralight text-foreground text-glow-white-thin"
                style={{ fontSize: centerFontPx }}
              >
                {centerMainNode}
              </span>
              <span className="mt-1 block text-sm text-white/60">
                {!isRunning ? (
                  "Paused"
                ) : freeRestStatus ? (
                  <span className="text-rest">
                    {freeRestStatus.left == null
                      ? "Resting "
                      : freeRestStatus.left > 0
                        ? "Rest "
                        : "Rest over "}
                    <b className="font-timer font-semibold tabular-nums">
                      {formatLapTime(
                        freeRestStatus.left == null
                          ? freeRestStatus.rested
                          : Math.abs(freeRestStatus.left),
                      )}
                    </b>
                    {freeRestStatus.left > 0 ? " left" : ""}
                  </span>
                ) : freeTraining.log.length > 0 ? (
                  <>
                    {"Since last "}
                    <b className="font-timer font-semibold tabular-nums text-white">
                      {formatLapTime(freeElapsed - lastLoggedAt(freeTraining.log))}
                    </b>
                  </>
                ) : (
                  freeSide ? "Log what you do beside" : "Log what you do below"
                )}
              </span>
            </div>
          ) : (
            <div className="text-center">
              {centerSub ? (
                <>
                  <span
                    className={cn(
                      "text-8xl font-timer font-bold text-text-base transition-colors duration-300",
                      isFinalCountdown && activeTokens.text,
                    )}
                    style={{
                      fontSize: centerFontPx,
                      // Heart rate alone in the centre takes its zone's colour,
                      // as on a sports watch.
                      ...(effectiveCenterMode === "hr" && hrZone ? { color: zoneColour(hrZone) } : null),
                    }}
                  >
                    {centerMainNode}
                  </span>
                  <span className="text-2xl font-timer text-muted-foreground block mt-1">
                    {centerSub}
                  </span>
                </>
              ) : (
                <span
                  className={cn(
                    "text-8xl font-timer font-extralight text-foreground text-glow-white-thin",
                    isFinalCountdown && activeTokens.text,
                  )}
                  style={{ fontSize: centerFontPx }}
                >
                  {centerMainNode}
                </span>
              )}
              {/* Same size as the unit label above, but brighter. The unit is
                  a label on the figure; the load is a fact you act on — it
                  tells you what to put on the bar — so it must not read as
                  smaller or fainter than the word "reps". */}
              {prescriptionLines.length > 0 && (
                // Raised above the tap-for-play/pause overlay, which covers
                // the whole ring: without this the load button is there but
                // every tap on it pauses the workout instead.
                <div className="relative z-30 mt-1.5 px-6 space-y-0.5 pointer-events-none [&>button]:pointer-events-auto">
                  {prescriptionLines.map((line) =>
                    line === loadDisplay && loadIsEditable ? (
                      <button
                        key={line}
                        type="button"
                        onClick={onEditLoad}
                        title="Adjust the load for this exercise"
                        className="block mx-auto font-timer tabular-nums leading-tight text-foreground/75 underline decoration-dotted decoration-foreground/25 underline-offset-4 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        {renderPrescription(line)}
                      </button>
                    ) : (
                      <span
                        key={line}
                        className="block font-timer tabular-nums leading-tight text-foreground/75"
                      >
                        {renderPrescription(line)}
                      </span>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Free training's chart, over the dial. Kept mounted while the dial
            shows, so the chart's ten minutes keep filling. */}
        {freeTraining && !isFinished && (
          <div
            aria-hidden={freeView !== "graph"}
            className={cn(
              "absolute inset-0 z-40 flex items-stretch bg-background transition-opacity duration-200",
              freeView === "graph" && (hasHr || hasPower)
                ? "opacity-100"
                : "pointer-events-none invisible opacity-0",
            )}
          >
            <FreeTrainingGraph
              bpm={currentBpm}
              watts={centerPowerW}
              hr={freeTraining.hr}
              zone={freeTraining.zone}
              subscribe={freeTraining.subscribe}
              hrSource={freeTraining.hrSource}
              basis={hrBasis}
            />
          </div>
        )}

        {/* Unified tap-for-play/pause overlay */}
        {!isFinished && !countingIn && (
          <button
            onPointerDown={handleRingPointerDown}
            onPointerUp={handleRingPointerUp}
            onPointerCancel={() => {
              ringPointerRef.current = null;
            }}
            onClick={handleRingClick}
            aria-label={tapOverlayLabel}
            // Let a horizontal drag that starts on the ring scroll the pager
            // (touch-action claims pans, not taps — tap-to-pause still fires).
            style={{ touchAction: "pan-x" }}
            className={cn("absolute inset-0 flex items-center justify-center", faceRound)}
          >
            {/* Paused: dim the ring and say so above the digits. A large
                icon in the centre sat on the colon and read as "02▮57".
                Landscape's wide, short face has no room above the digits,
                so the badge sits in its top-left corner instead. */}
            {isPaused && !tapFeedback && (
              <div className={cn("absolute inset-0 bg-black/40", faceRound)}>
                <span
                  className={cn(
                    "absolute inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/50 font-semibold uppercase tracking-[0.12em] text-white animate-pulse-fade",
                    landscape
                      ? "left-2 top-2 px-2 py-0.5 text-[10px]"
                      : "left-1/2 top-[17%] -translate-x-1/2 px-3 py-1 text-xs",
                  )}
                  style={{ animationDuration: `${pausedPulseDurationMs}ms` }}
                >
                  <Pause className="h-3.5 w-3.5 fill-current" />
                  Paused
                </span>
              </div>
            )}
            {tapFeedback && (
              <div
                className={cn(
                  "absolute inset-0 flex items-center justify-center animate-fade-out",
                  faceRound,
                  tapFeedback === "confirm" ? "bg-secondary/15" : "bg-black/40",
                )}
              >
                {tapFeedback === "pause" ? (
                  <Pause className="h-20 w-20 text-white" />
                ) : tapFeedback === "confirm" ? (
                  <Check className="h-20 w-20 text-secondary-foreground" />
                ) : (
                  <Play className="h-20 w-20 text-white fill-current ml-2" />
                )}
              </div>
            )}
          </button>
        )}
      </div>

      {landscape && (
        <SegmentFace
          // Counting in, the step's bar drains with the count; the squares
          // under it stay as they'll be, so nothing moves when the step starts.
          items={
            countingIn
              ? [{ type: "countin", current: countInRemaining, total: countInTotal }, ...ringItems.slice(1)]
              : ringItems
          }
          running={isRunning}
          className="px-1"
          style={{ width: faceW }}
        />
      )}

      {/* Adding a weight lives under the ring, not inside it: a dashed pill
          in the ring competed with the rep count and sat on the stroke. */}
      {showAddLoad && !readOnly && (
        <button
          type="button"
          onClick={onEditLoad}
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-white/25 px-4 py-1.5 text-sm text-white/70 hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4" />
          Add weight
        </button>
      )}

      {/* Reps actually done (#97): the set just finished, in the same quiet
          spot as Add weight, only once you're off the bar. */}
      {lastRepsSet && onEditReps && !isRepBased && !isFinished && !readOnly && (
        <button
          type="button"
          onClick={onEditReps}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-sm text-white/70 hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Change reps for ${lastRepsSet.label} set ${lastRepsSet.setNumber}`}
        >
          <span className="truncate">
            {lastRepsSet.label} · set {lastRepsSet.setNumber}:{" "}
            <span className="font-semibold text-white">{lastRepsSet.actual} reps</span>
          </span>
          <Pencil className="h-3.5 w-3.5 shrink-0" />
        </button>
      )}

      {/* Controls — directly under the ring for thumb-reach on mobile; on a
          phone on its side they sit at the foot of the right-hand column
          (#149), where there's height to spare. */}
      {!landscape && controlsEl}

      {freeTraining && !isFinished && (freeRest && freeTraining.onRestTarget ? (
        // How long this rest is: open until you're ready, or a countdown.
        // The one picked is used for the next rest too.
        <div className="-mt-1 flex flex-wrap items-center justify-center gap-1" role="group" aria-label="Rest length">
          {REST_TARGETS.map((t) => (
            <button
              key={t ?? "open"}
              type="button"
              onClick={() => freeTraining.onRestTarget(t)}
              aria-pressed={(freeRest.target || null) === t}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                (freeRest.target || null) === t
                  ? "bg-rest/20 text-rest ring-1 ring-rest/50"
                  : "bg-white/10 text-white/60 hover:text-white",
              )}
            >
              {t ? formatLapTime(t) : "Open"}
            </button>
          ))}
        </div>
      ) : (
        <p className="-mt-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
          {displayedExerciseName}
        </p>
      ))}

      {freeTraining && !isFinished && !readOnly && !freeSide && (
        <FreeTrainingLog
          log={freeTraining.log}
          list={freeTraining.list}
          onChange={freeTraining.onChange}
          checklist={freeTraining.checklist}
          onChecklistChange={freeTraining.onChecklistChange}
          checklistSessions={freeTraining.checklistSessions}
        />
      )}

      {!wide && nextUpEl}
      {!wide && outlineEl}
      {!wide && footerEl}
      </div>
      {/* end scrolling middle zone */}
    </>
  );

  if (landscape) {
    return (
      <div
        className={cn(
          "relative mx-auto grid w-full gap-x-4 overflow-hidden px-3 py-2",
          // Immersive, the page itself can't scroll (body overflow: hidden),
          // so the layout has to fit the screen and each column scrolls on
          // its own. A bare h-full had no definite parent height and grew
          // past the screen, cutting off Stop and the controls at the foot.
          isImmersive ? "h-[var(--coach-safe-vh)] max-h-full" : "h-full",
        )}
        style={{ gridTemplateColumns: "minmax(0, 1fr) min(20rem, 40%)", gridTemplateRows: "minmax(0, 1fr)" }}
      >
        <div className="relative flex min-h-0 min-w-0 flex-col items-center gap-1 overflow-y-auto overscroll-contain text-center">
          {coachColumn}
        </div>
        <aside
          className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain border-l border-border/60 pl-4 text-left"
          // "safe": centred while it fits; once it's taller than the screen it
          // starts at the top, so all of it can be scrolled to.
          style={{ justifyContent: "safe center" }}
        >
          {sidePanel}
          {statsPanel ? (
            <div className="grid grid-cols-2 gap-2">
              {currentBpm != null && (
                <StatTile
                  label="Heart rate"
                  sub={hrZone ? `Zone ${hrZone}` : "bpm"}
                  wide={!activeGroupInfo}
                >
                  <span style={hrZone ? { color: zoneColour(hrZone) } : undefined}>
                    {Math.round(currentBpm)}
                  </span>
                </StatTile>
              )}
              {activeGroupInfo && (
                <StatTile
                  label={activeGroupInfo.label || "Round"}
                  sub="round"
                  wide={currentBpm == null}
                >
                  {`${activeGroupInfo.currentIndex + 1}/${activeGroupInfo.totalIterations}`}
                </StatTile>
              )}
              {nextUpLabel && (
                <div className="col-span-2 rounded-xl border border-border/60 bg-card/60 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Up next
                  </p>
                  <p className="text-xl font-bold leading-tight">
                    {nextUpLabel}
                    {nextUpValue && (
                      <span className="ml-2 font-normal text-muted-foreground">{nextUpValue}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          ) : (
            nextUpEl
          )}
          {outlineEl}
          <div className="[&>div]:justify-start [&>div]:mb-0">{footerEl}</div>
          <div className="mt-auto">{controlsEl}</div>
          {actionsEl}
        </aside>
      </div>
    );
  }

  if (freeSide) {
    return (
      <div
        className={cn(
          "relative mx-auto grid w-full gap-x-4 overflow-hidden px-3 py-2",
          isImmersive ? "h-[var(--coach-safe-vh)] max-h-full" : "h-full",
        )}
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gridTemplateRows: "minmax(0, 1fr)" }}
      >
        <div className="relative flex min-h-0 min-w-0 flex-col items-center justify-center gap-2 overflow-y-auto overscroll-contain text-center">
          {coachColumn}
        </div>
        <aside className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain border-l border-border/60 pl-4 text-left">
          <FreeTrainingLog
            log={freeTraining.log}
            list={freeTraining.list}
            onChange={freeTraining.onChange}
            checklist={freeTraining.checklist}
            onChecklistChange={freeTraining.onChecklistChange}
            checklistSessions={freeTraining.checklistSessions}
          />
          <div className="mt-auto">{actionsEl}</div>
        </aside>
      </div>
    );
  }

  if (desktop) {
    return (
      <div className="relative mx-auto grid h-full w-full max-w-6xl grid-cols-[minmax(0,1fr)_22rem] gap-x-8 overflow-x-hidden p-6">
        <div className="relative flex min-w-0 flex-col items-center gap-2 text-center">
          {coachColumn}
        </div>
        <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto border-l border-border/60 pl-6 text-left">
          {nextUpEl}
          <div className="[&>div]:justify-start">{footerEl}</div>
          {(plan || standaloneTimer) && !freeTraining && (
            <SessionPlanOutline
              plan={plan}
              currentPath={currentPath}
              loopCounters={loopCounters}
              phaseLog={phaseLog}
              currentPhaseElapsedSec={currentPhaseElapsedSec}
              standaloneTimer={standaloneTimer}
              upcomingOnly={isSetupScreen}
              maxHeight="max-h-[60vh]"
            />
          )}
          <div className="mt-auto">{actionsEl}</div>
        </aside>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // overflow-x-hidden: the ring's stroke glow and the phase-hand-off
        // bloom bleed past the ring's own box (see below) and can exceed this
        // container's width on narrow viewports. Without this, that overflow
        // produces a genuine horizontal scrollbar — and once any scrollbar
        // axis is set to auto (isImmersive's overflow-y-auto below), the
        // other axis' default `visible` computes to `auto` too (CSS
        // overflow-x/y interaction), turning silent bleed into a scrollable
        // axis nobody actually wants.
        "relative flex flex-col items-center p-4 text-center max-w-lg mx-auto gap-2 overflow-x-hidden",
        // max-h-full: inside the Coach/equipment pager the pane reserves a
        // strip at its foot for the pane tabs, and a bare viewport height
        // would run straight through it, putting Stop workout under the tabs
        // (#148). Outside the pager there is no definite parent height, so
        // the cap falls away and the viewport height stands.
        isImmersive
          ? "h-[var(--coach-safe-vh)] max-h-full overflow-y-auto overscroll-contain"
          : "h-full",
      )}
    >
      {coachColumn}
      {!wide && actionsEl}
    </div>
  );

};

export default WorkoutCoachView;
